// v4 (spec-v4-structure-and-stage.md 3.2, 5.1, 5.3 "Character", 6.1, 6.5; ticket V4-C4): one character, at
// /k/:collectionId/cast/:characterId (a series' cast) and /p/:projectId/cast/:characterId (a video's; the id may be
// the script's slug, as the next step links it).
//
// The sheet viewer shows a version's 14 panels as Angles / Faces / Poses, with its locked colours. Every version is
// listed with its state (being drawn, waiting for your OK, approved, older, didn't work, discarded), any two can be
// compared side by side, and a version waiting for an OK has Approve / Discard right under the viewer. Older looks,
// a changed description and a sheet being drawn (live, followed by character id: sheet jobs have no project) are
// notices, never folded. Making a sheet or redoing a part always shows its price first (V4-C2's quote).
// The description editor carries data-unsaved="true" while it has unsaved edits, so the Director's stage doesn't
// cover it (stage bring-back guard).
import { useEffect, useMemo, useState } from "react";
import {
  canRedoPart,
  compareRows,
  comparePair,
  compareSummary,
  defaultVersion,
  findCharacter,
  groupOf,
  jobPart,
  olderNotice,
  PANEL_GROUPS,
  partCredits,
  SHEET_CREDITS,
  sheetButton,
  sheetProgress,
  VERSION_SHORT,
  VERSION_TONE,
  versionLine,
  versionBodyThumb,
  recutLine,
  characters,
  groupOfPart,
  type CharacterDetail,
  type CharacterInfo,
  type ComparePair,
  type PanelGroup,
  type SheetPart,
  type SheetVersion,
} from "../characters";
import { useChannelCollections, useCurrentChannel, type Crumb } from "../collections";
import { SheetConfirm, startSheet, useSheetJobs, VoicePick } from "../components/Cast";
import { Crumbs } from "../components/Crumbs";
import { Download } from "../components/Download";
import { Empty, ErrorNote, fmtCredits, Icon, Sheet, Spinner, useLoad } from "../components/bits";
import { Fold } from "../components/Fold";
import { Notice } from "../components/Notice";
import { UpdateOlderSheet, useOlderQuote } from "../components/OlderLook";
import { Shell } from "../components/Shell";
import { api } from "../api";
import { director } from "../director";
import { foldSummary } from "../fold";
import { PERSON_PHOTO_CONSENT } from "../legal/consent";
import { Link } from "../router";
import { bump, get as storeGet, toast, useRefresh, useStore } from "../store";
import type { Asset, JobView } from "../types";

type Props = { characterId: string; collectionId?: string; projectId?: string };
type Ask = { what: "all" | SheetPart; lead?: string; from?: string } | null;

export function CharacterScreen({ characterId, collectionId, projectId }: Props) {
  const tick = useRefresh("characters");
  const at = { collectionId: collectionId ?? null, projectId: projectId ?? null };
  const { data: d, setData, error, loading, reload } = useLoad<CharacterDetail>(() => findCharacter(at, characterId), [characterId, collectionId, projectId, tick]);
  const crumbs = useCrumbs(at, d?.character ?? null);

  useEffect(() => {
    if (!d) return;
    const c = d.character;
    const waiting = d.versions.find((v) => v.status === "draft");
    director.context(
      `The user is looking at ${c.name}'s character page (character ${c.id}${c.collectionId ? `, in collection ${c.collectionId}` : c.projectId ? `, in project ${c.projectId}` : ""}): ` +
        `${c.approved ? `v${c.approved.n} is approved` : "no approved sheet"}${waiting ? `, v${waiting.n} is waiting for their OK` : ""}${d.olderShots.length ? `, ${d.olderShots.length} shots still use an older look` : ""}.`,
    );
  }, [d?.character.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error && !d)
    return (
      <Shell>
        <div className="page">
          <Crumbs items={crumbs} />
          {error.toLowerCase().includes("not found") ? (
            <Empty title="This character isn't here" action={<Link to={backTo(at)} className="btn btn-primary">Go back</Link>}>
              They may have been removed from the cast, or the link belongs to another account.
            </Empty>
          ) : (
            <ErrorNote text={`Couldn't open this character. ${error}`} retry={reload} />
          )}
        </div>
      </Shell>
    );
  if (!d)
    return (
      <Shell>
        <div className="page">{loading && <Spinner label="Opening the character" />}</div>
      </Shell>
    );
  return (
    <Shell>
      <CharacterPage key={d.character.id} d={d} crumbs={crumbs} at={at} onCharacter={(c) => setData({ ...d, character: { ...d.character, ...c } })} />
    </Shell>
  );
}

const backTo = (at: { collectionId: string | null; projectId: string | null }) =>
  at.collectionId ? `/k/${encodeURIComponent(at.collectionId)}` : at.projectId ? `/p/${encodeURIComponent(at.projectId)}?tab=cast` : "/";

function useCrumbs(at: { collectionId: string | null; projectId: string | null }, c: CharacterInfo | null): Crumb[] {
  const channel = useCurrentChannel();
  const cols = useChannelCollections(channel?.id);
  const { data: p } = useLoad(() => (at.projectId ? api.project(at.projectId).catch(() => null) : Promise.resolve(null)), [at.projectId]);
  return useMemo(() => {
    const out: Crumb[] = [];
    const ch = (p?.channelId ? storeGet().me?.channels.find((x) => x.id === p.channelId) : null) ?? channel;
    if (ch) out.push({ label: ch.name || "Your channel", to: `/c/${encodeURIComponent(ch.id)}` });
    if (at.collectionId) out.push({ label: cols?.find((k) => k.id === at.collectionId)?.name ?? "Collection", to: `/k/${encodeURIComponent(at.collectionId)}` });
    if (at.projectId) out.push({ label: p?.title ?? "Video", to: `/p/${encodeURIComponent(at.projectId)}?tab=cast` });
    out.push({ label: c?.name ?? "Character", to: null });
    return out;
  }, [channel?.id, cols, p?.id, p?.title, c?.name, at.collectionId, at.projectId]); // eslint-disable-line react-hooks/exhaustive-deps
}

// ------------------------------------------------------------------ the page

function CharacterPage({ d, crumbs, at, onCharacter }: { d: CharacterDetail; crumbs: Crumb[]; at: { collectionId: string | null; projectId: string | null }; onCharacter: (c: Partial<CharacterInfo>) => void }) {
  const c = d.character;
  const jobs = useSheetJobs(c.id);
  const balance = useStore((s) => s.balance);
  const [shownN, setShownN] = useState<number | null>(() => defaultVersion(d.versions, c.approved?.n ?? null)?.n ?? null);
  const [group, setGroup] = useState<PanelGroup>("angles");
  const [pair, setPair] = useState<ComparePair | null>(null);
  const [ask, setAsk] = useState<Ask>(null);
  const [updating, setUpdating] = useState(false);
  const [discarding, setDiscarding] = useState<SheetVersion | null>(null);
  const [busy, setBusy] = useState(false);
  const { data: quote } = useLoad(() => characters.quote(c.id, "all").catch(() => null), [c.id]);
  const older = useOlderQuote(c.id, d.olderShots.length);

  // a new version (a sheet just started, or one that just finished) moves the viewer onto it
  const newest = d.versions[0]?.n ?? null;
  useEffect(() => {
    setShownN(defaultVersion(d.versions, c.approved?.n ?? null)?.n ?? null);
  }, [newest, d.versions[0]?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = d.versions.find((v) => v.n === shownN) ?? defaultVersion(d.versions, c.approved?.n ?? null);
  const making = d.versions.find((v) => v.status === "making") ?? null;
  const allPrice = quote?.credits ?? SHEET_CREDITS;
  const onePrice = partCredits(quote ?? null);
  const hasSheet = d.versions.some((v) => v.status !== "discarded" && v.status !== "failed");
  const redoPart = canRedoPart(d.versions);
  const groupPart = groupOf(group).part;
  const failed = d.versions[0]?.status === "failed" ? d.versions[0] : null;

  const changed = (said: string) => {
    bump("characters", "project", "projects", "collections");
    director.context(`The user ${said} on ${c.name}'s character page by tapping.`);
  };
  const approve = async (v: SheetVersion) => {
    setBusy(true);
    try {
      const next = await characters.approve(c.id, v.n);
      onCharacter(next);
      toast(`${c.name}'s v${v.n} is approved. Scenes with ${c.name} can be drawn now.`, "good");
      changed(`approved v${v.n} of ${c.name}'s sheet`);
    } catch (e) {
      toast(`Couldn't approve it. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };
  const discard = async (v: SheetVersion) => {
    setDiscarding(null);
    setBusy(true);
    try {
      await characters.discard(c.id, v.n);
      toast(`v${v.n} is discarded.`, "good");
      changed(`discarded v${v.n} of ${c.name}'s sheet`);
    } catch (e) {
      toast(`Couldn't discard it. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };
  // 2026-11-04 (Keaton's report: Mom's faces were cut wrong): cut this part's panels again from its picture, free
  const recut = async (v: SheetVersion, part: SheetPart) => {
    setBusy(true);
    try {
      if (!characters.recut) throw new Error("This build can't do that yet.");
      const r = await characters.recut(c.id, v.n, part);
      const line = recutLine(c.name, v.n, r);
      toast(line.text, line.tone);
      changed(`cut the ${groupOf(group).word} of v${v.n} of ${c.name}'s sheet again (free, nothing redrawn)`);
    } catch (e) {
      toast(`Couldn't cut it again. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };
  const run = async (a: NonNullable<Ask>, note: string) => {
    setAsk(null);
    const ok = await startSheet(c, { part: a.what, ...(note ? { note } : {}), ...(a.from ? { from: { assetId: a.from } } : {}) }, a.what === "all" ? allPrice : onePrice);
    if (ok) setPair(null);
  };

  return (
    <div className="page charpage" data-character={c.id}>
      <Crumbs items={crumbs} />
      <header className="charpage-head">
        <h1 className="page-title">{c.name}</h1>
        {shown && (
          <span className={`vchip vchip-${VERSION_TONE[shown.status]}`}>
            v{shown.n} · {VERSION_SHORT[shown.status]}
          </span>
        )}
      </header>

      {/* never folded: a sheet being drawn, a sheet waiting, older looks, a changed description, a failed sheet */}
      {making && <Making name={c.name} jobs={jobs} v={making} />}
      {failed && !making && <Notice tone="error" code="SHEET_FAILED" text={`v${failed.n} didn't work. The credits came back.`} action={<button data-action="character.sheet" type="button" className="btn btn-quiet btn-small" onClick={() => setAsk({ what: "all" })}>Try again</button>} />}
      {d.olderShots.length > 0 && (
        <Notice
          tone="warning"
          code="OLDER_LOOK"
          text={olderNotice(d.olderShots)}
          action={
            <button data-action="older.update" type="button" className="btn btn-quiet btn-small" onClick={() => setUpdating(true)}>
              Update these{older ? ` · ${fmtCredits(older.credits)}` : ""}
            </button>
          }
        />
      )}
      {c.lookChanged && !making && (
        <Notice tone="info" code="LOOK_CHANGED" text="The description changed since this sheet. Make a new sheet?" action={<button data-action="character.sheet" type="button" className="btn btn-quiet btn-small" onClick={() => setAsk({ what: "all" })}>Make it · {fmtCredits(allPrice)}</button>} />
      )}

      <div className="charpage-grid">
        <section className="charpage-viewer" aria-label={`${c.name}'s sheet`}>
          {shown?.status === "draft" && (
            <div className="sheet-decide">
              <p>
                {c.approved ? `v${shown.n} is waiting for your OK. v${c.approved.n} stays in use until you approve it.` : `v${shown.n} is waiting for your OK. Scenes with ${c.name} wait for it.`}
              </p>
              <div className="sheet-decide-acts">
                <button data-action="character.discard" type="button" className="btn btn-quiet" disabled={busy} onClick={() => setDiscarding(shown)}>
                  Discard
                </button>
                <button data-action="character.approve" type="button" className="btn btn-primary" disabled={busy} onClick={() => void approve(shown)}>
                  <Icon name="check" size={16} /> Approve v{shown.n}
                </button>
              </div>
            </div>
          )}
          <div className="sheet-tabs" role="tablist" aria-label="Sheet panels">
            {PANEL_GROUPS.map((g) => (
              <button data-action="character.show" key={g.id} type="button" role="tab" aria-selected={group === g.id} className={`sheet-tab${group === g.id ? " is-on" : ""}`} onClick={() => setGroup(g.id)}>
                {g.label}
              </button>
            ))}
            {d.versions.filter((v) => v.status !== "discarded" && v.status !== "failed").length > 1 && (
              <button data-action="character.show" type="button" className={`sheet-tab sheet-compare${pair ? " is-on" : ""}`} aria-pressed={!!pair} onClick={() => setPair(pair ? null : comparePair(d.versions, c.approved?.n ?? null))}>
                <Icon name="compare" size={15} /> Compare
              </button>
            )}
          </div>

          {pair ? (
            <CompareView pair={pair} group={group} versions={d.versions} onPair={setPair} />
          ) : shown ? (
            <>
              <Panels v={shown} group={group} />
              {shown.palette.length > 0 && <Palette v={shown} />}
              <div className="sheet-under">
                <Download url={shown.sheets[groupPart]?.downloadUrl} label={`${c.name}'s ${groupOf(group).word}, v${shown.n}`} variant="button" className="sheet-download" />
                {shown.sheets[groupPart] && shown.status !== "making" && shown.status !== "discarded" && (
                  <button data-action="character.recut" type="button" className="btn btn-quiet btn-small sheet-recut" disabled={busy} onClick={() => void recut(shown, groupPart)} title="Cut the panels again from this picture. Free: nothing is redrawn.">
                    Cut the {groupOf(group).word} again
                  </button>
                )}
              </div>
              {shown.note && <p className="sheet-note">Asked for: “{shown.note}”</p>}
            </>
          ) : (
            <div className="sheet-empty">
              <span className="sheet-empty-mono" aria-hidden>
                {c.name.slice(0, 1)}
              </span>
              <p>No sheet yet. It's three pictures (four angles, six faces and four poses) that every scene {c.name} is in is drawn from.</p>
            </div>
          )}

        </section>

        <aside className="charpage-side">
          <LookFold c={c} hasSheet={hasSheet} price={allPrice} onSaved={(look) => onCharacter({ look, lookChanged: hasSheet || c.lookChanged })} onAsk={setAsk} />
          <VoiceFold c={c} onSaved={(patch) => onCharacter(patch)} />
          <ExtrasFold c={c} approved={d.versions.find((v) => v.n === c.approved?.n) ?? null} onChanged={() => bump("characters")} />
          <SourceFold c={c} hasSheet={hasSheet} onPick={(assetId) => setAsk({ what: "all", from: assetId, lead: `The next sheet starts from this picture. The approved sheet stays until you approve the new one.` })} />
          <Fold id="character.versions" title="Versions" count={d.versions.length} summary={foldSummary(d.versions.slice(0, 2).map((v) => versionLine(v)))}>
            <Versions versions={d.versions} shownN={shown?.n ?? null} approvedN={c.approved?.n ?? null} onShow={(n) => (setPair(null), setShownN(n))} onCompare={(v) => setPair(shown && shown.n !== v.n ? (v.n < shown.n ? { left: v, right: shown } : { left: shown, right: v }) : comparePair(d.versions, c.approved?.n ?? null))} />
          </Fold>

          <div className="charpage-acts">
            {!making && (
              <button data-action="character.sheet" type="button" className={`btn ${hasSheet ? "btn-quiet" : "btn-primary"}`} onClick={() => setAsk({ what: "all" })}>
                {sheetButton("all", allPrice, hasSheet)}
              </button>
            )}
            {!making && redoPart && (
              <button data-action="character.redo-part" type="button" className="btn btn-quiet" onClick={() => setAsk({ what: groupPart })}>
                {sheetButton(groupPart, onePrice, true)}
              </button>
            )}
            {quote?.model.short && <p className="field-hint">Drawn with {quote.model.short}.{quote.said ? ` ${quote.said}` : ""}</p>}
          </div>
        </aside>
      </div>

      {ask && (
        <SheetConfirm
          name={c.name}
          what={ask.what}
          credits={ask.what === "all" ? allPrice : onePrice}
          hasSheet={hasSheet}
          askNote
          lead={ask.lead}
          onClose={() => setAsk(null)}
          onConfirm={(note) => void run(ask, note)}
        />
      )}
      {updating && <UpdateOlderSheet characterId={c.id} name={c.name} onClose={() => setUpdating(false)} />}
      {discarding && (
        <Sheet title={`Discard v${discarding.n}?`} onClose={() => setDiscarding(null)}>
          <div className="sheet-body">
            <p>It goes from the list of sheets to choose from. {c.approved ? `v${c.approved.n} stays approved.` : `${c.name} still needs an approved sheet before scenes are drawn.`}</p>
            {balance != null && discarding.credits > 0 && <p className="muted">The {discarding.credits} credits it cost don't come back.</p>}
            <div className="sheet-actions">
              <button type="button" className="btn btn-quiet" onClick={() => setDiscarding(null)}>
                Keep it
              </button>
              <button type="button" className="btn btn-primary" data-autofocus onClick={() => void discard(discarding)}>
                Discard
              </button>
            </div>
          </div>
        </Sheet>
      )}
      <p className="charpage-back">
        <Link to={backTo(at)} className="btn btn-quiet btn-small">
          <Icon name="back" size={14} /> {at.collectionId ? "Back to the series" : "Back to the video"}
        </Link>
      </p>
    </div>
  );
}

// ------------------------------------------------------------------ viewer parts

function Panels({ v, group, label }: { v: SheetVersion; group: PanelGroup; label?: string }) {
  const g = groupOf(group);
  const drawing = v.status === "making";
  return (
    <ul className={`sheet-panels sheet-panels-${group}`} aria-label={label ?? `${g.label}, v${v.n}`}>
      {g.panels.map((p) => {
        const url = v.panels[p.id]?.url ?? null;
        return (
          <li key={p.id}>
            <span className={`sheet-panel${!url && drawing ? " is-drawing" : ""}`}>{url ? <img src={url} alt={p.label} loading="lazy" /> : <span className="sheet-panel-empty">{drawing ? "Drawing…" : "—"}</span>}</span>
            <span className="sheet-panel-label">{p.label}</span>
          </li>
        );
      })}
    </ul>
  );
}

function Palette({ v }: { v: SheetVersion }) {
  return (
    <ul className="sheet-palette" aria-label="Locked colours">
      {v.palette.map((c) => (
        <li key={c.name + c.hex}>
          <span className="sheet-swatch" style={{ background: c.hex }} aria-hidden />
          <span>
            {c.name} <span className="muted">{c.hex.toUpperCase()}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function CompareView({ pair, group, versions, onPair }: { pair: ComparePair; group: PanelGroup; versions: SheetVersion[]; onPair: (p: ComparePair) => void }) {
  const rows = compareRows(pair, group);
  const opts = versions.filter((v) => v.status !== "discarded" && v.status !== "failed");
  const pick = (side: "left" | "right", n: number) => {
    const v = versions.find((x) => x.n === n);
    if (v) onPair({ ...pair, [side]: v });
  };
  return (
    <div className="sheet-compare-view">
      <div className="sheet-compare-head">
        {(["left", "right"] as const).map((side) => (
          <label key={side} className="select sheet-compare-pick">
            <select value={pair[side].n} onChange={(e) => pick(side, Number(e.target.value))} aria-label={side === "left" ? "Left version" : "Right version"}>
              {opts.map((v) => (
                <option key={v.n} value={v.n}>
                  v{v.n} · {VERSION_SHORT[v.status]}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
      <p className="sheet-compare-sum">{compareSummary(pair)}</p>
      <ul className="sheet-compare-rows">
        {rows.map((r) => (
          <li key={r.id}>
            <span className="sheet-panel">{r.left ? <img src={r.left} alt={`${r.label}, v${pair.left.n}`} loading="lazy" /> : <span className="sheet-panel-empty">—</span>}</span>
            <span className="sheet-panel">{r.right ? <img src={r.right} alt={`${r.label}, v${pair.right.n}`} loading="lazy" /> : <span className="sheet-panel-empty">—</span>}</span>
            <span className="sheet-panel-label">{r.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A sheet being drawn: one bar per part, live from the job events (followed by character id). */
function Making({ name, jobs, v }: { name: string; jobs: JobView[]; v: SheetVersion }) {
  const pct = Math.round(sheetProgress(jobs) * 100);
  return (
    <div className="sheet-making" role="status" aria-live="polite">
      <p>
        <strong>
          Drawing {name}'s v{v.n}
        </strong>
        {jobs.length ? ` · ${pct}%` : "…"}
      </p>
      {jobs.length > 0 && (
        <ul>
          {jobs.map((j) => {
            const part = jobPart(j);
            return (
              <li key={j.id}>
                <span>{part ? groupOfPart(part).label : (j.step ?? "A part")}</span>
                <span className="sheet-bar" aria-hidden>
                  <span style={{ width: `${Math.max(4, Math.round(j.progress * 100))}%` }} />
                </span>
                <span className="muted">{j.status === "queued" ? "waiting" : `${Math.round(j.progress * 100)}%`}</span>
              </li>
            );
          })}
        </ul>
      )}
      <p className="muted">{v.sheets.turnaround ? "" : "The faces and poses start once the angles are in. "}You can leave this page; it carries on.</p>
    </div>
  );
}

function Versions({ versions, shownN, approvedN, onShow, onCompare }: { versions: SheetVersion[]; shownN: number | null; approvedN: number | null; onShow: (n: number) => void; onCompare: (v: SheetVersion) => void }) {
  if (!versions.length) return <p className="muted">No sheets yet.</p>;
  const comparable = versions.filter((v) => v.status !== "discarded" && v.status !== "failed").length > 1;
  return (
    <ul className="sheet-versions">
      {versions.map((v) => {
        const thumb = versionBodyThumb(v); // the body here; the cast tiles show the face
        const on = v.n === shownN;
        return (
          <li key={v.n} className={`sheet-ver sheet-ver-${v.status}${on ? " is-on" : ""}`}>
            <button data-action="character.show" type="button" className="sheet-ver-main" onClick={() => onShow(v.n)} aria-pressed={on} disabled={v.status === "discarded" && !thumb}>
              <span className="sheet-ver-thumb">{thumb ? <img src={thumb} alt="" loading="lazy" /> : <span>v{v.n}</span>}</span>
              <span className="sheet-ver-text">
                <strong>{versionLine(v)}</strong>
                {v.n === approvedN && v.status !== "approved" ? null : v.note ? <span className="muted">“{v.note}”</span> : v.basedOn ? <span className="muted">From v{v.basedOn}</span> : v.legacy ? <span className="muted">Made before versions</span> : null}
              </span>
            </button>
            {comparable && !on && v.status !== "discarded" && v.status !== "failed" && (
              <button data-action="character.show" type="button" className="btn btn-quiet btn-small" onClick={() => onCompare(v)}>
                Compare
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ------------------------------------------------------------------ folds

/** The description. While it has unsaved edits it carries data-unsaved="true" (the stage won't cover it). */
function LookFold({ c, hasSheet, price, onSaved, onAsk }: { c: CharacterInfo; hasSheet: boolean; price: number; onSaved: (look: string) => void; onAsk: (a: Ask) => void }) {
  const [look, setLook] = useState(c.look);
  const [busy, setBusy] = useState(false);
  useEffect(() => setLook(c.look), [c.look]);
  const dirty = look.trim() !== c.look.trim();
  const save = async () => {
    setBusy(true);
    try {
      await characters.edit(c.id, { look: look.trim() });
      onSaved(look.trim());
      bump("characters", "project");
      director.context(`The user changed ${c.name}'s description on the character page by tapping: "${look.trim()}".${hasSheet ? " The approved sheet stays until a new one is approved." : ""}`);
      if (hasSheet) onAsk({ what: "all", lead: `${c.name}'s description changed. Make a new sheet so the pictures match? The approved sheet stays until you approve the new one.` });
      else toast("Saved.", "good");
    } catch (e) {
      toast(`Couldn't save it. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Fold id="character.look" title="Description" summary={c.look || "Not described yet"} defaultOpen={!c.look}>
      <div className="look-edit" data-unsaved={dirty ? "true" : undefined}>
        <label className="field">
          <span className="field-label">What {c.name} looks like</span>
          <textarea value={look} onChange={(e) => setLook(e.target.value)} rows={3} maxLength={600} placeholder="A small red fox with a striped scarf and big, worried eyes" />
          <span className="field-hint">{hasSheet ? `Changing it keeps the current sheet; you'll be asked about a new one (${price} credits).` : "The sheet is drawn from this."}</span>
        </label>
        {dirty && (
          <div className="look-edit-acts">
            <button type="button" className="btn btn-quiet btn-small" onClick={() => setLook(c.look)} disabled={busy}>
              Undo
            </button>
            <button data-action="cast.edit" type="button" className="btn btn-primary btn-small" onClick={() => void save()} disabled={busy || !look.trim()}>
              {busy ? "Saving" : "Save"}
            </button>
          </div>
        )}
      </div>
    </Fold>
  );
}

function VoiceFold({ c, onSaved }: { c: CharacterInfo; onSaved: (p: Partial<CharacterInfo>) => void }) {
  const [picking, setPicking] = useState(false);
  const [voice, setVoice] = useState(c.voice);
  useEffect(() => setVoice(c.voice), [c.voice]);
  const dirty = voice.trim() !== c.voice.trim();
  const save = async (patch: { voice?: string; voiceId?: string }, said: string) => {
    try {
      const next = await characters.edit(c.id, patch);
      onSaved({ voice: next.voice, voiceId: next.voiceId, voiceName: next.voiceName });
      bump("characters", "project");
      director.context(`The user ${said} on ${c.name}'s character page by tapping.`);
      return true;
    } catch (e) {
      toast(`Couldn't save it. ${(e as Error).message}`, "bad");
      return false;
    }
  };
  return (
    <Fold id="character.voice" title="Voice" summary={[c.voiceName ?? "Not cast yet", c.voice].filter(Boolean).join(" · ")}>
      <div className="look-edit" data-unsaved={dirty ? "true" : undefined}>
        <div className="ccard-voice">
          <Icon name="mic" size={15} />
          <span>
            <strong>{c.voiceName ?? (c.voiceId ? "Cast" : "Not cast yet")}</strong>
            <span className="muted">{c.voice || "Cast from how they're described"}</span>
          </span>
          <button data-action="cast.voice" type="button" className="btn btn-quiet btn-small" onClick={() => setPicking(true)}>
            {c.voiceId ? "Change" : "Pick a voice"}
          </button>
        </div>
        <label className="field">
          <span className="field-label">How {c.name} sounds</span>
          <input value={voice} onChange={(e) => setVoice(e.target.value)} maxLength={200} placeholder="Bright, a little nervous, young" />
        </label>
        {dirty && (
          <div className="look-edit-acts">
            <button data-action="cast.edit" type="button" className="btn btn-primary btn-small" onClick={() => void save({ voice: voice.trim() }, `described ${c.name}'s voice as "${voice.trim()}"`)}>
              Save
            </button>
          </div>
        )}
      </div>
      {picking && <VoicePick who={{ key: c.id, name: c.name, look: c.look, voice: c.voice, voiceId: c.voiceId }} taken={new Set()} onClose={() => setPicking(false)} onPick={(v) => save({ voiceId: v.voiceId }, `cast ${v.name} as ${c.name}'s voice`)} />}
    </Fold>
  );
}

const SOURCE_WORD = { description: "The description", drawing: "A drawing", photo: "A photo" } as const;

/**
 * What the sheet starts from. A photo of a real person needs the permission tick (Q8: never a public figure, never
 * anyone under 18); picking a new picture asks again before anything is made.
 */
/**
 * 2026-11-03 (Keaton's reports: "a hat with a reference picture, or a different outfit, without redoing the whole
 * sheet"; "isolate the beanie and reuse it"): pictures of what a character wears or carries, named the way the scenes
 * say it. A scene whose words about them name one sends it with their sheet. Free.
 */
function ExtrasFold({ c, approved, onChanged }: { c: CharacterInfo; approved: SheetVersion | null; onChanged: () => void }) {
  const extras = approved?.extras ?? [];
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [pick, setPick] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { data: pics } = useLoad<Asset[]>(() => (adding ? api.library({ kind: "image" }).catch(() => []) : Promise.resolve([])), [adding]);
  const add = async () => {
    if (!pick || !label.trim()) return;
    setBusy(true);
    try {
      await characters.addExtra(c.id, { assetId: pick, label: label.trim() });
      director.context(`The user added "${label.trim()}" to ${c.name}'s outfits and accessories by tapping (free): scenes that say ${c.name} has it send that picture with the sheet.`);
      toast(`"${label.trim()}" is one of ${c.name}'s now.`, "good");
      setAdding(false);
      setLabel("");
      setPick(null);
      onChanged();
    } catch (e) {
      toast(`Couldn't add it. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Fold id="character.extras" title="Outfits and accessories" count={extras.length} summary={extras.map((x) => x.label).join(", ") || "None yet"}>
      {!approved ? (
        <p className="muted">Once {c.name} has an approved sheet, add a hat, a coat or another outfit here without remaking the sheet.</p>
      ) : (
        <>
          {!!extras.length && (
            <ul className="extras-list">
              {extras.map((x) => (
                <li key={x.assetId}>
                  {x.url ? <img src={x.url} alt="" loading="lazy" /> : <span className="photo-blank" />}
                  <span>{x.label}</span>
                  <button
                    data-action="character.extra-remove"
                    type="button"
                    className="icon-btn"
                    aria-label={`Take off ${x.label}`}
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      await characters.removeExtra(c.id, x.assetId).catch((e: unknown) => toast(`Couldn't take it off. ${(e as Error).message}`, "bad"));
                      director.context(`The user took "${x.label}" off ${c.name}'s outfits and accessories by tapping.`);
                      setBusy(false);
                      onChanged();
                    }}
                  >
                    <Icon name="close" size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="field-hint">A scene that says {c.name} has it (“{c.name} in the {extras[0]?.label.replace(/^(the|a|an)\s+/i, "") || "beanie"}”) sends that picture with the sheet. To cut one out of a scene&apos;s picture, make it a prop from that scene first.</p>
          {adding ? (
            <div className="extras-add">
              <label className="field">
                <span className="field-label">What the scenes call it</span>
                <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={40} placeholder="the red raincoat" autoComplete="off" />
              </label>
              {pics && !pics.length && <p className="muted">No pictures in your library yet.</p>}
              {!!pics?.length && (
                <ul className="photos" aria-label="Your pictures">
                  {pics.map((a) => (
                    <li key={a.id}>
                      <button type="button" className={`photo${pick === a.id ? " is-on" : ""}`} aria-pressed={pick === a.id} onClick={() => setPick(a.id)} aria-label={a.name}>
                        {a.thumbUrl || a.url ? <img src={a.thumbUrl || a.url || ""} alt="" loading="lazy" /> : <span className="photo-blank" />}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="sheet-actions">
                <button type="button" className="btn btn-quiet btn-small" onClick={() => setAdding(false)}>
                  Not now
                </button>
                <button data-action="character.extra-add" type="button" className="btn btn-primary btn-small" disabled={busy || !pick || !label.trim()} onClick={() => void add()}>
                  Add it
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="btn btn-quiet btn-small" onClick={() => setAdding(true)}>
              <Icon name="plus" size={14} /> Add an outfit or accessory
            </button>
          )}
        </>
      )}
    </Fold>
  );
}

function SourceFold({ c, hasSheet, onPick }: { c: CharacterInfo; hasSheet: boolean; onPick: (assetId: string) => void }) {
  const [picking, setPicking] = useState(false);
  const s = c.source;
  const person = s.kind === "photo" && (s.subject ?? "person") === "person";
  const summary = `${SOURCE_WORD[s.kind]}${person ? (s.consent ? " · permission given" : " · needs permission") : s.kind === "photo" && s.subject ? ` of a ${s.subject}` : ""}`;
  return (
    <Fold id="character.source" title="Starts from" summary={summary}>
      <div className="source-box">
        {s.url ? <img src={s.url} alt="The starting picture" className="source-img" /> : s.kind !== "description" ? <span className="source-img source-img-none">Picture</span> : null}
        <div>
          <p>{s.kind === "description" ? `${c.name} is drawn from the description alone.` : s.kind === "drawing" ? `The sheet redraws your drawing as a clean model sheet, keeping its shapes and colours.` : `The sheet turns the photo into a character, keeping hair, colours, clothes and recognisable features.`}</p>
          {person && <p className={s.consent ? "source-ok" : "field-error"}>{s.consent ? <><Icon name="check" size={14} /> This is them, or you have their permission.</> : "This photo of a person needs their permission before a sheet can be made."}</p>}
          <button data-action="cast.voice" type="button" className="btn btn-quiet btn-small" onClick={() => setPicking(true)}>
            <Icon name="image" size={14} /> {hasSheet ? "Start the next sheet from a picture" : "Start from a picture"}
          </button>
        </div>
      </div>
      {picking && (
        <PickSource
          name={c.name}
          photoOfPerson={person}
          onClose={() => setPicking(false)}
          onPick={(id) => {
            setPicking(false);
            onPick(id);
          }}
        />
      )}
    </Fold>
  );
}

function PickSource({ name, photoOfPerson, onClose, onPick }: { name: string; photoOfPerson: boolean; onClose: () => void; onPick: (assetId: string) => void }) {
  const { data, loading } = useLoad<Asset[]>(() => api.library({ kind: "image" }).catch(() => []), []);
  const [id, setId] = useState<string | null>(null);
  const [kind, setKind] = useState<"drawing" | "person" | "other">(photoOfPerson ? "person" : "drawing");
  const [consent, setConsent] = useState(false);
  const ok = !!id && (kind !== "person" || consent);
  return (
    <Sheet title={`Start ${name}'s sheet from a picture`} onClose={onClose}>
      <div className="sheet-body">
        {loading && !data && <Spinner label="Loading your pictures" />}
        {data && !data.length && (
          <p className="muted">
            No pictures in your <Link to="/library">Library</Link> yet. Add one there first.
          </p>
        )}
        {!!data?.length && (
          <ul className="photos" aria-label="Your pictures">
            {data.map((a) => (
              <li key={a.id}>
                <button type="button" className={`photo${id === a.id ? " is-on" : ""}`} aria-pressed={id === a.id} onClick={() => setId(a.id)} aria-label={a.name}>
                  {a.thumbUrl || a.url ? <img src={a.thumbUrl || a.url || ""} alt="" loading="lazy" /> : <span className="photo-blank" />}
                </button>
              </li>
            ))}
          </ul>
        )}
        <fieldset className="source-kind">
          <legend className="field-label">What is it?</legend>
          {(
            [
              ["drawing", "A drawing or a design"],
              ["person", "A photo of a real person"],
              ["other", "A photo of a pet or a thing"],
            ] as const
          ).map(([k, label]) => (
            <label key={k} className="check">
              <input type="radio" name="source-kind" checked={kind === k} onChange={() => setKind(k)} disabled={photoOfPerson && k !== "person"} /> {label}
            </label>
          ))}
        </fieldset>
        {kind === "person" && (
          <label className="check consent">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>
              {PERSON_PHOTO_CONSENT.label}{" "}
              <a href={PERSON_PHOTO_CONSENT.link.href} target="_blank" rel="noreferrer">
                {PERSON_PHOTO_CONSENT.link.text}
              </a>
            </span>
          </label>
        )}
        <div className="sheet-actions">
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Not now
          </button>
          <button data-action="character.from-picture" type="button" className="btn btn-primary" disabled={!ok} onClick={() => id && onPick(id)}>
            Use this picture
          </button>
        </div>
      </div>
    </Sheet>
  );
}
